import { useCallback, useEffect, useMemo, useRef } from "react";

import { WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { IEventTrackerProp } from "../../../../types/events";
import { LoadQuoteValues } from "../../load-quote";
import { trackLoadQuoteFormChange, trackLoadQuoteLocationInput } from "../../analytics/trackLoadQuoteFormAnalytics";
import { useTrackWebV2FieldOnce } from "../../analytics/homepageV2WebAnalytics";
import { useCompactSheetAutoFocus } from "../../hooks/useCompactSheetAutoFocus";
import { getQuoteFormPickupToDropFocusDelayMs } from "../../methods/homepageV2CompactSheet";
import { useLoadQuoteForm } from "./useLoadQuoteForm";
import {
    focusQuoteFormField,
    getFirstQuoteFormInvalidFieldId,
    getRouteQuoteFormValidationOptions,
    QuoteFormFieldId,
    QuoteFormValidationOptions
} from "./validateQuoteForm";
import { LoadQuoteFormVariant } from "./loadQuoteForm.constants";
import { QuoteFormLocationMessages } from "./fields/QuoteFormLocationField";
import { useAppUserPlaceInfo } from "../../../../hooks/useAppUserPlaceInfo";

type UseLoadQuoteLocationFormParams = Pick<IEventTrackerProp, "trackEvent" | "eventData"> & {
    values: LoadQuoteValues;
    onChange: (patch: Partial<LoadQuoteValues>) => void;
    sourceScreen: typeof WEB_V2_SOURCE_SCREENS.HOME_PAGE | typeof WEB_V2_SOURCE_SCREENS.LOAD_FORM;
    isRemoteConfigLoaded: boolean;
    t: (key: string, opts?: object) => string;
    /** Prefill pickup from app context (homepage + compact route step). */
    prefillFromContext?: boolean;
    /** Compact route step: focus first invalid field after sheet mount. */
    autoFocusOnMount?: boolean;
    /** Compact route step: advance focus to drop after user selects pickup. */
    focusDropOnPickupSelect?: boolean;
    validationOptions?: QuoteFormValidationOptions;
    fieldIdMap?: Partial<Record<QuoteFormFieldId, string>>;
    variant?: LoadQuoteFormVariant;
};

export function useLoadQuoteLocationForm({
    values,
    onChange,
    sourceScreen,
    isRemoteConfigLoaded,
    t,
    trackEvent,
    eventData,
    prefillFromContext = false,
    autoFocusOnMount = false,
    focusDropOnPickupSelect = false,
    validationOptions: validationOptionsOverride,
    fieldIdMap,
    variant = "hero"
}: UseLoadQuoteLocationFormParams) {
    const userPlaceInfo = useAppUserPlaceInfo();
    const didPrefillSourceRef = useRef(false);
    const prevPickupRef = useRef(values.source?.place_id);
    const skipProgrammaticDropFocusRef = useRef(false);
    const valuesRef = useRef(values);
    valuesRef.current = values;

    const eventDataWithScreen = useMemo(
        () => ({ ...eventData, source_screen: sourceScreen }),
        [eventData, sourceScreen]
    );
    const { trackFieldOnce } = useTrackWebV2FieldOnce(trackEvent, eventDataWithScreen);

    const validationOptions = useMemo(
        () =>
            validationOptionsOverride ??
            (variant === "routeStep"
                ? getRouteQuoteFormValidationOptions(isRemoteConfigLoaded, t)
                : { isRemoteConfigLoaded, t }),
        [isRemoteConfigLoaded, t, validationOptionsOverride, variant]
    );

    useEffect(() => {
        if (!prefillFromContext || didPrefillSourceRef.current || !userPlaceInfo?.place_id || values.source?.place_id) {
            return;
        }

        didPrefillSourceRef.current = true;
        skipProgrammaticDropFocusRef.current = true;
        onChange({ source: userPlaceInfo });
    }, [onChange, prefillFromContext, userPlaceInfo, values.source?.place_id]);

    const handleChange = useCallback(
        (patch: Partial<LoadQuoteValues>) => {
            trackLoadQuoteFormChange(trackFieldOnce, patch, {
                sourceScreen,
                isRemoteConfigLoaded,
                previousValues: valuesRef.current
            });
            onChange(patch);
        },
        [isRemoteConfigLoaded, onChange, sourceScreen, trackFieldOnce]
    );

    const { isFormValid } = useLoadQuoteForm({
        values,
        onChange: handleChange,
        isRemoteConfigLoaded,
        t,
        skipCapacityValidation: validationOptions.skipCapacityValidation
    });

    const locationMessages = useMemo<QuoteFormLocationMessages>(
        () => ({
            enterLocation: t("homepageV2:hero.quoteForm.enterLocation"),
            noSuchLocation: t("homepageV2:hero.quoteForm.noSuchLocation"),
            getMoreCharactersLabel: (count: number) => t("homepageV2:hero.quoteForm.moreCharacters", { count })
        }),
        [t]
    );

    const handleLocationInput = useCallback(
        (field: "pickup" | "drop", inputValue: string) => {
            trackLoadQuoteLocationInput(trackFieldOnce, field, inputValue, sourceScreen);
        },
        [sourceScreen, trackFieldOnce]
    );

    const focusField = useCallback(
        (fieldId: QuoteFormFieldId, options?: { delay?: number }) => {
            focusQuoteFormField(fieldId, {
                fieldIdMap,
                delay: options?.delay,
                scrollIntoView: Boolean(fieldIdMap)
            });
        },
        [fieldIdMap]
    );

    const autoFocusFieldId = useMemo(() => {
        if (!autoFocusOnMount) {
            return undefined;
        }

        const firstInvalidField = getFirstQuoteFormInvalidFieldId(values, validationOptions);
        const fieldId = firstInvalidField ?? "quote-pickup-city";
        return fieldIdMap?.[fieldId] ?? fieldId;
    }, [autoFocusOnMount, fieldIdMap, validationOptions, values]);

    useCompactSheetAutoFocus(autoFocusFieldId, {
        enabled: autoFocusOnMount,
        scrollIntoView: Boolean(fieldIdMap)
    });

    useEffect(() => {
        if (!focusDropOnPickupSelect) {
            return;
        }

        const currentPickup = values.source?.place_id;
        const hadPickup = prevPickupRef.current;

        if (!hadPickup && currentPickup) {
            if (skipProgrammaticDropFocusRef.current) {
                skipProgrammaticDropFocusRef.current = false;
            } else {
                focusField("quote-drop-city", { delay: getQuoteFormPickupToDropFocusDelayMs() });
            }
        }

        prevPickupRef.current = currentPickup;
    }, [focusDropOnPickupSelect, focusField, values.source?.place_id]);

    const focusFirstInvalidField = useCallback(() => {
        const firstInvalidField = getFirstQuoteFormInvalidFieldId(values, validationOptions);
        if (firstInvalidField) {
            focusField(firstInvalidField);
        }
    }, [focusField, validationOptions, values]);

    return {
        handleChange,
        handleLocationInput,
        locationMessages,
        isFormValid,
        validationOptions,
        focusFirstInvalidField
    };
}
